import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { syncPendingChanges, getPendingChanges } from '@/utils/offlineStorage';
import { supabase } from '@/integrations/supabase/client';

export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    const checkPending = async () => {
      const changes = await getPendingChanges();
      setPendingCount(changes.length);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    checkPending();

    const interval = setInterval(checkPending, 5000);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (isOnline && pendingCount > 0 && !syncing) {
      handleSync();
    }
  }, [isOnline, pendingCount]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncPendingChanges(supabase);
      const changes = await getPendingChanges();
      setPendingCount(changes.length);
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  if (isOnline && pendingCount === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <Alert className={`backdrop-blur-md ${
        isOnline 
          ? 'bg-primary/10 border-primary/20' 
          : 'bg-destructive/10 border-destructive/20'
      }`}>
        <div className="flex items-center gap-3">
          {isOnline ? (
            syncing ? (
              <RefreshCw className="h-5 w-5 animate-spin text-primary" />
            ) : (
              <Wifi className="h-5 w-5 text-primary" />
            )
          ) : (
            <WifiOff className="h-5 w-5 text-destructive" />
          )}
          <AlertDescription className="flex-1">
            {!isOnline && (
              <span className="font-medium text-destructive">
                Sie sind offline. Änderungen werden lokal gespeichert.
              </span>
            )}
            {isOnline && syncing && (
              <span className="font-medium text-primary">
                Synchronisiere {pendingCount} Änderung(en)...
              </span>
            )}
            {isOnline && !syncing && pendingCount > 0 && (
              <span className="font-medium text-primary">
                {pendingCount} Änderung(en) ausstehend
              </span>
            )}
          </AlertDescription>
        </div>
      </Alert>
    </div>
  );
};
